//  Copyright (c) 2020 Proto Labs, Inc. All Rights Reserved.
import { FeatureStoreInfo } from '@/models/FeatureStoreInfo';

/* eslint-disable require-jsdoc */
export const IMSample = Object.freeze({
	maxQuantity: 500,
});

export const CustomerSupplied = Object.freeze({
	customerSupplied: 'CUSTOMERSUPPLIED',
});

export const ItemStatus = Object.freeze({
	new: 'New',
	inAnalysis: 'InAnalysis',
	needsReview: 'NeedsReview',
	needsRevision: 'NeedsRevision',
	needsConfiguration: 'NeedsConfiguration',
	nonOrderable: 'NonOrderable',
	orderable: 'Orderable',
	expired: 'Expired',
	obsolete: 'Obsolete',
	instantAnalyzing: 'InstantAnalyzing',
});

export const LineItemCategory = Object.freeze({
	fee: 'FEE',
});

export const StatusConfig = {
	[ItemStatus.new]: {
		textKey: 'quoting.lineItemStatus.needsConfigure',
		disableViewAdvisories: true,
		disableUploadRevision: true,
		enableConfigure: true,
	},
	[ItemStatus.inAnalysis]: {
		textKey: 'quoting.lineItemStatus.underReview',
		disableViewAdvisories: true,
		disableConfigurePart: true,
		disableUploadRevision: true,
		disablePartOptions: false,
	},
	[ItemStatus.needsReview]: {
		textKey: 'quoting.lineItemStatus.requiresApproval',
		icon: 'warning_small',
		iconClass: 'needs-review',
		enableViewAdvisories: true,
		enableUploadRevision: true,
	},
	[ItemStatus.nonOrderable]: {
		textKey: 'quoting.lineItemStatus.needsAttention',
		icon: 'warning_small',
		iconClass: 'non-orderable',
		enableViewAdvisories: true,
		enableUploadRevision: true,
	},
	[ItemStatus.needsRevision]: {
		textKey: 'quoting.lineItemStatus.needsAttention',
		icon: 'warning_small',
		iconClass: 'non-orderable',
		enableViewAdvisories: true,
		enableUploadRevision: true,
	},
	[ItemStatus.orderable]: {
		textKey: 'quoting.lineItemStatus.ready',
		icon: 'check_circle_small',
		iconClass: 'orderable',
		enableViewAdvisories: true,
		enableUploadRevision: true,
	},
	[ItemStatus.expired]: {
		textKey: 'quoting.lineItemStatus.expired',
		enableRequestQuote: true,
		enableUploadRevision: true,
	},
	[ItemStatus.needsConfiguration]: {
		textKey: 'quoting.lineItemStatus.needsConfigure',
		icon: 'warning_small',
		iconClass: 'needs-review',
		enableConfigure: true,
		enableViewAdvisories: true,
		enableUploadRevision: true,
	},
	[ItemStatus.instantAnalyzing]: {
		textKey: 'quoting.lineItemStatus.underReview',
		icon: 'warning_small',
		iconClass: 'instant-analyzing',
		disableViewAdvisories: true,
		disableConfigurePart: true,
		disableUploadRevision: true,
		disablePartOptions: false,
	},
} as any;

export const StatusChecklistItemCode = Object.freeze({
	requestAnalysisAndPricing: 'RequestAnalysisAndPricing',
	configurePart: 'ConfigurePart',
	selectMoldLife: 'SelectMoldLife',
	chooseMillOrLathe: 'ChooseMillOrLathe',
	chooseOrDismissThreading: 'ChooseOrDismissThreading',
	reviewAndApproveAdvisories: 'ReviewAndApproveAdvisories',
	acceptGateAndEjectorLayouts: 'AcceptGateAndEjectorLayouts',
	uploadRevisionOrRemove: 'UploadRevisionOrRemove',
	reviewAndApproveAdvisoriesForPPR: 'ReviewAndApproveAdvisoriesForPPR',
});

export const StatusChecklistItemState = Object.freeze({
	unchecked: 'Unchecked',
	checked: 'Checked',
	redAlert: 'RedAlert',
	yellowAlert: 'YellowAlert',
});

export const PricingAdjustmentCodes = Object.freeze({
	Shipping: 'SHIPPING',
	Expedite: 'EXPEDITE',
	NoRushDiscount: 'NORUSHDISCOUNT',
	Setup: 'SETUP',
	Mold: 'MOLD',
	MoldMod: 'MOLDMOD',
	MaterialChange: 'MATRLCHRG',
	ThermalPostCure: 'THMPOSTCUR',
	MoldUpgrade: 'SVCLVLUPGD',
	CadWork: 'CAD',
	DialIn: 'DIALIN',
	HeatTreat: 'HEAT_TREAT_FEE',
	Finishing: 'FINISH',
	SetupFee: 'SETUP',
	SpecialMaterial: 'Special Material',
	Substrate: 'SUBSTRATE',
	SupplementalFreight: 'SUPP_FREIGHT',
	FirstArticleInspection: 'CMMCTQINSPECTION',
	CapabilityStudyReport: 'CMMCTQCAPABILITY',
	Other: 'OTHER',
});

export const WeightClasses = Object.freeze({
	Pound: 'lb',
	Kilogram: 'kg',
});

export const AdditionalProductCodes = Object.freeze({
	FirstArticleInspection: 'CMMINSPECT',
	CapabilityStudyReport: 'CMMCAPABILITY',
});

export const AdditionalProductTypes = Object.freeze({
	Inspection: 'INSPECTION',
	InspectionAddon: 'INSPECTIONADDON',
	AdditionalInspection: 'ADDITIONALINSPECTION',
	ImProductionAgreement: 'IMPRODUCTIONAGREEMENT',
});

export const ManualReviewCodes = Object.freeze({
	Shipping: 'SHIPPING',
});

export const ProductionAgreementTypes = Object.freeze({
	moldAgreement: 'IMMoldAgreement',
});

export const WellKnownProductionAgreements = Object.freeze({
	DecideLater: 'DECIDELATER',
});

export const WellKnownProductionAgreementConfigurationIds = Object.freeze({
	DecideLater: 'IMPRODUCTIONAGREEMENT:DECIDELATER',
});

export const WellKnownProductionAgreementProductIds = Object.freeze({
	DecideLater: 'IMPRODUCTIONAGREEMENT:DECIDELATER',
});

export const Regions = Object.freeze({
	EMEA: 'EMEA',
	AMER: 'AMER',
	APAC: 'APAC',
});

export const CurrencyCodes = Object.freeze({
	USD: 'USD',
	EUR: 'EUR',
	GBP: 'GBP',
});

export const AmPm = Object.freeze({
	AM: 'AM',
	PM: 'PM',
	// eslint-disable-next-line no-inline-comments
	useShippingLevelAMPM: false, // This is just in case they bring back shipping level AM/PM
});

export class ManufacturingService {
	id: number;
	name: string;

	constructor(id: number, name: string) {
		this.id = id;
		this.name = name;
	}
}

class ManufacturingServiceEnum {
	THREEDP = new ManufacturingService(0, '3DP');
	IM = new ManufacturingService(1, 'IM');
	IMOVERMOLDING = new ManufacturingService(1, 'IMOVERMOLDING');
	IMINSERTMOLDING = new ManufacturingService(1, 'IMINSERTMOLDING');
	CNC = new ManufacturingService(2, 'CNC');
	SM = new ManufacturingService(3, 'SM');
	SECOP = new ManufacturingService(4, 'SECOP');
}

export const manufacturingService = new ManufacturingServiceEnum();

export const ManufacturingServices = Object.freeze([
	manufacturingService.THREEDP.name,
	manufacturingService.IM.name,
	manufacturingService.CNC.name,
	manufacturingService.SM.name,
	manufacturingService.SECOP.name,
]);

export const ManufacturingSubServices = Object.freeze({
	CNC: {
		Protolabs: 'PROTOLABS',
		MyRapid: 'MYRAPID',
		Network: 'NETWORK',
	},
	IM: {
		IM: 'IM',
		IMOverMolding: 'IMOVERMOLDING',
		IMInsertMolding: 'IMINSERTMOLDING',
	},
});

class ConfigurationClass {
	id: number;
	name: string;
	service: ManufacturingService;
	isMultiComponent: boolean;
	instantAnalysisEnabled: boolean;
	partExpirationEnabled: boolean;

	constructor(
		id: number,
		name: string,
		service: ManufacturingService,
		isMultiComponent = false,
		instantAnalysisEnabled = false,
		partExpirationEnabled = true,
	) {
		this.id = id;
		this.name = name;
		this.service = service;
		this.isMultiComponent = isMultiComponent;
		this.instantAnalysisEnabled = instantAnalysisEnabled;
		this.partExpirationEnabled = partExpirationEnabled;
	}
}

export type ConfigurationClassTypes =
	| '3DPPART'
	| 'IMPART'
	| 'CNCPART'
	| 'IMOVERMOLDASSEMBLY'
	| 'IMINSERTASSEMBLY';

export const ConfigurationClassTypes = Object.freeze({
	THREEDPPART: '3DPPART' as ConfigurationClassTypes,
	IMPART: 'IMPART' as ConfigurationClassTypes,
	CNCPART: 'CNCPART' as ConfigurationClassTypes,
	IMOVERMOLDASSEMBLY: 'IMOVERMOLDASSEMBLY' as ConfigurationClassTypes,
	IMINSERTASSEMBLY: 'IMINSERTASSEMBLY' as ConfigurationClassTypes,
});

class ConfigurationClasses {
	printedPart = new ConfigurationClass(
		0,
		ConfigurationClassTypes.THREEDPPART,
		manufacturingService.THREEDP,
		false,
		true,
		false,
	);
	imPart = new ConfigurationClass(1, ConfigurationClassTypes.IMPART, manufacturingService.IM);
	cncPart = new ConfigurationClass(
		2,
		ConfigurationClassTypes.CNCPART,
		manufacturingService.CNC,
		false,
		true,
	);
	imOvermoldAssembly = new ConfigurationClass(
		3,
		ConfigurationClassTypes.IMOVERMOLDASSEMBLY,
		manufacturingService.IM,
		true,
	);
	imInsertAssembly = new ConfigurationClass(
		4,
		ConfigurationClassTypes.IMINSERTASSEMBLY,
		manufacturingService.IM,
		true,
	);
	sheetMetal = new ConfigurationClass(5, 'SMPART', manufacturingService.SM);
	secOp = new ConfigurationClass(6, 'SECONDARYOPERATION', manufacturingService.SECOP);

	match(configurationClass: string) {
		const allTypes = {
			[this.printedPart.name]: this.printedPart,
			[this.imPart.name]: this.imPart,
			[this.cncPart.name]: this.cncPart,
			[this.imInsertAssembly.name]: this.imInsertAssembly,
			[this.imOvermoldAssembly.name]: this.imOvermoldAssembly,
			[this.sheetMetal.name]: this.sheetMetal,
			[this.secOp.name]: this.secOp,
		};

		return allTypes[configurationClass] ? allTypes[configurationClass] : null;
	}

	isThreeDp(configurationClass: string) {
		const threeDpTypes = {
			[this.printedPart.name]: this.printedPart,
		};

		return !!threeDpTypes[configurationClass];
	}

	isIM(configurationClass: string) {
		const imTypes = {
			[this.imPart.name]: this.imPart,
			[this.imInsertAssembly.name]: this.imInsertAssembly,
			[this.imOvermoldAssembly.name]: this.imOvermoldAssembly,
		};

		return !!imTypes[configurationClass];
	}

	isCNC(configurationClass: string) {
		const cncTypes = {
			[this.cncPart.name]: this.cncPart,
		};

		return !!cncTypes[configurationClass];
	}

	isSheetMetal(configurationClass: string) {
		const sheetMetalTypes = {
			[this.sheetMetal.name]: this.sheetMetal,
		};

		return !!sheetMetalTypes[configurationClass];
	}

	isOvermold(configurationClass: string) {
		const overmoldTypes = {
			[this.imOvermoldAssembly.name]: this.imOvermoldAssembly,
		};

		return !!overmoldTypes[configurationClass];
	}

	isInsertMold(configurationClass: string) {
		const insertMoldTypes = {
			[this.imInsertAssembly.name]: this.imInsertAssembly,
		};

		return !!insertMoldTypes[configurationClass];
	}

	isSpecialtyIM(configurationClass: string) {
		const imSpecialtyTypes = {
			[this.imInsertAssembly.name]: this.imInsertAssembly,
			[this.imOvermoldAssembly.name]: this.imOvermoldAssembly,
		};

		return !!imSpecialtyTypes[configurationClass];
	}
}

export const configurationClass = new ConfigurationClasses();

export const translationKeyMap = {
	IM: 'common.im',
	IMPART: 'common.im',
	IMOVERMOLDASSEMBLY: 'common.om',
	IMINSERTASSEMBLY: 'common.insm',
	IMOVERMOLDING: 'common.om',
	CNC: 'common.cnc',
	CNCPART: 'common.cnc',
	'3DP': 'common.3dp',
	'3DPPART': 'common.3dp',
};

export const ProductionAgreementsTransKeyMap = {
	DECIDELATER: 'catalogService.prodAgreements.decideLater',
	LIMITED: 'catalogService.prodAgreements.limited',
	UNLIMITED: 'catalogService.prodAgreements.unlimited',
};

export const UploadTypes = Object.freeze({
	CDFM: 'CDFM',
	CUSTOMER: 'CUSTOMER',
});

export const DfmRoutes = Object.freeze({
	ThreadingExpanded: 'threadingExpanded',
	LeftOverMaterialExpanded: 'lomConfigurationSelectorExpanded',
});

class FieldLengthValue {
	min: number;
	max: number;

	constructor(min: number, max: number) {
		this.min = min;
		this.max = max;
	}
}

export const FieldLengths = Object.freeze({
	PartName: new FieldLengthValue(0, 100),
	RevisionName: new FieldLengthValue(0, 10),
	ProjectName: new FieldLengthValue(0, 40),
	Note: new FieldLengthValue(0, 4500),
	VatId: new FieldLengthValue(0, 15), // TODO: Remove old VatId code
	VatNumber: new FieldLengthValue(0, 18),
	ShippingReferenceNumber: new FieldLengthValue(0, 50),
	Carrier: new FieldLengthValue(0, 48),
	CarrierAccountNumber: new FieldLengthValue(0, 32),
	CarrierContactInfo: new FieldLengthValue(0, 100),
	Email: new FieldLengthValue(0, 100),
	Name: new FieldLengthValue(0, 128),
	Phone: new FieldLengthValue(0, 30),
	CompanyName: new FieldLengthValue(0, 80),
	Password: new FieldLengthValue(8, 100),
	Street: new FieldLengthValue(0, 128),
	City: new FieldLengthValue(0, 40),
	StateProvince: new FieldLengthValue(0, 64),
	PostalCode: new FieldLengthValue(0, 10),
	exportDeclarationIndustry: new FieldLengthValue(0, 50),
	exportDeclarationDescription: new FieldLengthValue(0, 1000),
	exportDeclarationSignee: new FieldLengthValue(0, 250),
	exportDeclarationJobDescription: new FieldLengthValue(0, 250),
	exportDeclarationFedTaxId: new FieldLengthValue(0, 50),
	exportDeclarationHarmonizedCommodityCode: new FieldLengthValue(0, 50),
	PurchaseOrderNumber: new FieldLengthValue(0, 20),
	ProfileEmail: new FieldLengthValue(0, 80),
	ProfileFirstName: new FieldLengthValue(0, 25),
	ProfileLastName: new FieldLengthValue(0, 25),
	ProfileTitle: new FieldLengthValue(0, 12),
	ProfilePhone: new FieldLengthValue(0, 30),
	ProfileCompany: new FieldLengthValue(0, 60),
	BillingAddress: new FieldLengthValue(0, 50),
	EORINumber: new FieldLengthValue(0, 30),
	ProductionPartDescription: new FieldLengthValue(0, 50),
	ProductionPartRevision: new FieldLengthValue(0, 10),
	ProductionPartNumber: new FieldLengthValue(0, 30),
	ProductionCatalogName: new FieldLengthValue(0, 40),
});

export const ClaimTypes = Object.freeze({
	Id: 'sub',
	OnBehalfOfActor: 'act',
	Locale: 'locale',
	ProtolabsUserRole: 'ent_usr_rl',
	ProtolabsExportControl: 'protolabs_export_control',
	Email: 'email',
});

export const OboClaimTypes = Object.freeze({
	Id: 'sub',
	Email: 'email',
	GivenName: 'given_name',
	Surname: 'family_name',
});

export const UserType = Object.freeze({
	GuestUser: 'Guest',
	DemoUser: 'Sample Quote',
	SignedInUser: 'Signed In',
});

export class OboEmployee {
	GivenName: string;
	Surname: string;
	Email: string;
	Id: string;

	constructor(dto: { [x: string]: string }) {
		this.GivenName = dto[OboClaimTypes.GivenName];
		this.Surname = dto[OboClaimTypes.Surname];
		this.Email = dto[OboClaimTypes.Email];
		this.Id = dto[OboClaimTypes.Id];
	}
}

export const MoreMenuOptions = Object.freeze({
	ChangeMaterials: 'changeMaterials',
	ViewAdvisories: 'viewAdvisories',
	UploadRevision: 'uploadRevision',
	RemoveFromQuote: 'removeFromQuote',
	MovePart: 'movePart',
	DuplicatePart: 'duplicatePart',
	PartRevisions: 'partRevisions',
});

export const ShippingTermsTypes = Object.freeze({
	Collect: 'CollectShipping',
	Prepaid: 'PrepaidShipping',
});

export const OrderFilterOptions = Object.freeze({
	AllOrders: 'allOrders',
	ThreeDPrinting: '3DPPART',
	CNCMachining: 'CNCPART',
	InjectionMolding: 'IM',
	SharedQuote: 'sharedQuote',
	AllTime: 'allTime',
	Last30DaysOrders: 'last30DaysOrders',
	Last3MonthsOrders: 'last3MonthsOrders',
});

export type FilterServicesType = '3dp' | 'im' | 'cnc';

export const FilterOptions = Object.freeze({
	all: 'all',
	cnc: 'cnc',
	im: 'im',
	'3dp': '3dp',
	sm: 'sm',
	// TODO delete this "threedp" constant when we delete production parts feature flags
	threedp: 'threedp',
	archived: 'archived',
	allTime: 'allTime',
	last30Days: 'last30Days',
	last90Days: 'last90Days',
	uploaded: 'uploaded',
	ordered: 'ordered',
	updatedDate: 'updatedDate',
	uploadedDate: 'uploadedDate',
	partName: 'partName',
});

export const PartLifeStage = Object.freeze({
	Variant: 'VARIANT',
	Configuration: 'CONFIGURATION',
	Part: 'PART',
	Cached: 'CACHED',
});

export const OrderProgressDefaults = Object.freeze({
	Min: 10,
	Max: 95,
});

export const OverlayStatus = Object.freeze({
	None: 'none',
	UnderReview: 'underReview',
	ExceedsCapabilities: 'exceedsCapabilities',
	Expired: 'expired',
	ExpiredNeedsRevision: 'expiredNeedsRevision',
	MustUpdateConfiguration: 'mustUpdateConfiguration',
	SelectMoldLife: 'selectMoldLife',
	ChooseMillOrLathe: 'chooseMillOrLathe',
	ManualQuote: 'manualQuote',
	InstantAnalyzing: 'instantAnalyzing',
	CleanPriceNotAvailable: 'cleanPriceNotAvailable',
});

export const LineItemPricingConcerns = Object.freeze({
	InternationalShipment: 'InternationalShipment',
	IndirectlyShipped: 'IndirectlyShipped',
	ExceedsMaxMillingMinutes: 'MillingMinutesExceedMaxJobLength',
	PricingAdjustmentNotPriced: 'PricingAdjustmentNotPriced',
	NoFulfillmentOptionsForSupplier: 'NoFulfillmentOptionsForSupplier',
});

export const GroupedShippingConcerns = Object.freeze({
	InvalidFulfillmentOption: 'InvalidFulfillmentOption',
});

export const QuoteConcerns = Object.freeze({
	InvalidAddress: 'InvalidAddress',
});

export const SearchFilters = Object.freeze({
	OrderNumber: 'orderNumber',
});

export const QuoteNumberPlaceHolder = '-';

export const LineItemDeliveryOptionsAppearances = Object.freeze({
	showOverlayCalendar: 'showOverlayCalendar',
	showCalendar: 'showCalendar',
	showDropdown: 'showDropdown',
});

export const SessionKeys = Object.freeze({
	UserLanguage: 'userLanguage',
});

export const EmptyGuid = Object.freeze({
	Value: '00000000-0000-0000-0000-000000000000',
});

export const ValidQuickLinkLanguages = new Set(['en', 'fr', 'de']);

export const OrderOnHold = Object.freeze({
	poRequiredCodes: {
		'400': { name: 'PO required', regions: [Regions.AMER, Regions.EMEA] },
		'701': { name: 'PO required', regions: [Regions.AMER, Regions.EMEA] },
		'913 Auto': {
			name: 'PunchOut Order - Hold for Mfg',
			regions: [Regions.AMER, Regions.EMEA],
		},
		'408': { name: 'PunchOut Order', regions: [Regions.AMER, Regions.EMEA] },
		'409': { name: 'PunchOut Order - Hold for Mfg', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'poRequired',
		translationHeaderCode: '400',
	},
	revisedPoRequiredCodes: {
		'401': { name: 'Revised PO needed', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'revisedPoRequired',
		translationHeaderCode: '401',
	},
	paymentRequiredCodes: {
		'402': { name: 'Prepayment required', regions: [Regions.AMER] },
		'901 Auto': { name: 'Prepayment required', regions: [Regions.AMER, Regions.EMEA] },
		'403': 'CBS payment required',
		translationToolTipCode: 'paymentRequired',
		translationHeaderCode: '402',
	},
	shippingAccountNeededCodes: {
		'404': { name: 'Shipping Account Needed', regions: [Regions.EMEA] },
		translationToolTipCode: 'shippingAccountNeeded',
		translationHeaderCode: '404',
	},
	pastDueInvoicesCodes: {
		'501': { name: 'Past due invoices', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'pastDueInvoices',
		translationHeaderCode: '501',
	},
	sampleApprovalNeededCodes: {
		'405': { name: 'Sample approval needed', regions: [Regions.AMER, Regions.EMEA] },
		'910 Auto': { name: 'Sample order has not shipped', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'sampleApprovalNeeded',
		translationHeaderCode: '405',
	},
	materialRequiredCodes: {
		'407': { name: 'CSI follow-up needed', regions: [Regions.AMER, Regions.EMEA] },
		'410': { name: 'CSI follow-up needed', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'materialRequired',
		translationHeaderCode: '407',
	},
	exportInformationRequiredCodes: {
		'602': { name: 'Customer completes export docs', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'exportInformationRequired',
		translationHeaderCode: '602',
	},
	creditLimitExceeded: {
		'500': { name: 'Credit limit exceeded', regions: [Regions.AMER, Regions.EMEA] },
		translationToolTipCode: 'creditLimitExceeded',
		translationHeaderCode: '500',
	},
});

export const MaxHoldValues = 4;

export const SortOrderTypes = Object.freeze({
	UploadOrder: 'uploadOrder',
	PartNameAtoZ: 'partNameAtoZ',
	PartNameZtoA: 'partNameZtoA',
});

export const Carriers = Object.freeze({
	DHL: 'DHL',
	FedEx: 'FedEx',
	UPS: 'UPS',
});

export const LineItemOptions = Object.freeze({
	CONFIGURE: 'configure',
	ADVISORIES: 'advisories',
	THREADING: 'threading',
	RENAME: 'rename',
	DUPLICATE: 'duplicate',
	CHANGE_UNITS: 'changeUnits',
	REVISE: 'revise',
	REVISIONS: 'revisions',
	MOVE: 'move',
	REMOVE_FROM_CATALOG: 'removeFromCatalog',
	ADD_TO_CATALOG: 'addToCatalog',
	REMOVE: 'remove',
	DETAILS: 'details',
});

export const Suppliers = Object.freeze({
	PROTOLABS: 'PROTOLABS',
	HUBS: 'HUBS',
	ESCHENLOHE: 'ESCHENLOHE',
	NEWHAMPSHIRE: 'NEWHAMPSHIRE',
});

export const MaxQuantities = Object.freeze({
	SAMPLEIM: 500,
	HUBS: 1000,
	DEFAULT: 999999,
});

export const Experiments = Object.freeze({
	ENABLE_CNC_INSTANT: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'pao_cnc_send_instant_analysis_complete',
		value: 'on',
	}),
	ENABLE_LEFT_OVER_MATERIAL: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'commerce_left_over_material',
		value: 'on',
	}),
	ENABLE_UPLOAD_PAGE_REPLACEMENT: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'commerce_upload_page_replacement_enabled',
		value: 'on',
	}),
	ENABLE_MILITARY_DUAL_USE: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_military_dual_use',
		value: 'on',
	}),
	ENABLE_ECOM_CONTACT_PRODUCTION_TEAM: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_show_contact_our_production_team',
		value: 'on',
	}),
	ENABLE_SCALED_INSPECTION_D3: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'scaled_inspection_d3',
		value: 'on',
	}),
	ENABLE_CAPABILITY_NEWHAMPSHIRE_CNC: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'capability_new_hampshire_cnc',
		value: 'enabled',
	}),
	SHOW_NEW_QUOTE_MODAL_QUESTION: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_show_new_quote_parts_project_question',
		loadExperimentEveryTime: true,
	}),
	ENABLE_SCALED_INSPECTION_D4: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'scaled_inspection_d4',
		value: 'on',
	}),
	ENABLE_WELLS_FARGO: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_use_wells_fargo',
		value: 'on',
	}),
	ENABLE_PRODUCTION_CATALOG: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_production_catalog',
		value: 'on',
	}),
	ENABLE_DFM_COMPONENTIZATION: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_dfm_componentization',
		value: 'on',
	}),
	ENABLE_ECOM_SUPPORT_CENTER: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_support_center',
		value: 'on',
	}),
	ENABLE_ECOM_PART_SHARING: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_part_sharing',
		value: 'on',
	}),
	ENABLE_PRODUCTION_CATALOG_SHARING: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_production_catalog_sharing',
		value: 'on',
	}),
	ENABLE_TEAM_PAGE: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_show_team_page',
		value: 'on',
	}),
	ENABLE_ORDER_SHARING: new FeatureStoreInfo({
		itemType: 'Experiment',
		featureId: 'ecom_order_sharing',
		value: 'on',
	}),
});

export const FeatureFlags = Object.freeze({
	ENABLE_USE_DB_ORDER_TABLES: new FeatureStoreInfo({
		itemType: 'FeatureFlag',
		featureId: 'use_new_order_tables',
		featureName: 'UseDbOrderTables',
	}),
	ENABLE_COMMERCE_USER_MISMATCH_SIGN_OUT: new FeatureStoreInfo({
		itemType: 'FeatureFlag',
		featureId: 'commerce_user_mismatch_sign_out',
		featureName: 'UserMismatchSignOut',
	}),
});

export const StartANewQuoteVariants = Object.freeze({
	VariantA: 'variant_a',
	VariantB: 'variant_b',
	VariantC: 'variant_c',
});

export const ConfiguratorScaledInspectionVersion = Object.freeze({
	D4: 'D4',
	D3: 'D3',
	D2: 'D2',
});

export const MaterialEstimateResources = Object.freeze({
	MATERIAL: 'Material',
});

export const LockDownStatusTypes = Object.freeze({
	ESCHENLOHE: 'ESCHENLOHE',
	CUSTOM_FINISH: 'CUSTOM_FINISH',
	SHARED_LOCK: 'SHARED_LOCK',
});

export const CarouselValues = Object.freeze({
	DeliveryOptionPx: 100,
	GroupedShippingIndexPx: 140,
	GroupedShippingIndexPaddingPx: 20,
	ManufacturedByPx: 180,
	ShowNetworkTilePx: 200,
	ShowNetworkTilePaddingPx: 30,
	ShowLeftSideLOM: 120,
	SameDayShippingcontentsPx: 340,
	SameDayShippingcontentsPaddingPx: 160,
	MinSlides: 3,
	MaxSlides: 10,
});

export const EmeaSourcedMaterials = Object.freeze(['True Silicone']);

export const ProtolabsAddresses = Object.freeze({
	US: 'Protolabs Inc. 5540 Pioneer Creek Dr. Maple Plain, MN 55359 United States',
	GB: 'Protolabs Ltd. Halesfield 8, Telford Shropshire TF74QN United Kingdom',
	DE: 'Protolabs Germany GmbH Hermann-Oberth-Straße 21 85640 Putzbrunn Germany',
	FR: 'PL Euro Services Limited - Protolabs Savoie-Technolac Parc Ouragan Bâtiment C <br />Rue du Lac Majeur - BP 331 <br />73377 Le Bourget du Lac Cedex France',
	IT: 'PL Euro Services Limited - Protolabs Via Biandrate, 24 28100 Novara Italy',
	SE: 'PL Euro Services Limited - Protolabs Sickla industrivag 3, 131 54 Nacka Sweden',
});

export const CapabilityConstants = Object.freeze({
	ShipToNotSupported: 'SHIP-TO-NOT-SUPPORTED',
	IsTooBig: 'IS-TOO-BIG',
	NotInstantEligible: 'NOT-INSTANT-ELIGIBLE',
	ThreadingNotSupported: 'THREAD-CONFIGURATION-NOT-SUPPORTED',
	QuickLink: 'QUICKLINK',
	MaterialLeftBehind: 'MATERIAL-LEFT-BEHIND',
	WouldMakeClean: 'WOULD-MAKE-CLEAN',
	CleanNotSupported: 'CLEAN-NOT-SUPPORTED',
	CapabilityInProcess: 'CAPABILITY-IN-PROCESS',
	InternalCornersRounded: 'INTERNAL-CORNERS-ROUNDED',
	MillingMinutesExceeded: 'MILLING-MINUTES-EXCEEDED',
	ExceedsExtents: 'EXCEEDS-EXTENTS',
});

export const CapabilityPriceBoundsConstants = Object.freeze({
	LowestDeliveredPiecePriceAtQuantity: 'LowestDeliveredPiecePriceAtQuantity',
	LowestDeliveredTotalPriceAtQuantity: 'LowestDeliveredTotalPriceAtQuantity',
});

export const CapabilityDeliveryBoundsConstants = Object.freeze({
	Fastest: 'Fastest',
});

export const HubsQuicklinkQuerystringVars = Object.freeze({
	QuoteNumber: 'quote_number',
	UserId: 'id',
	UtmSource: 'utm_source',
	UtmCompaign: 'utm_campaign',
	UtmContent: 'utm_content',
});

export const CleanPartStatus = Object.freeze({
	CustomerChoice: 'CustomerChoice',
});

export const RouteNames = Object.freeze({
	Review: 'Review',
	DemoReview: 'DemoReview',
	Configure: 'Configure',
	DemoConfigure: 'DemoConfigure',
});

export const UnsupportedLanguages = Object.freeze(['sv-SE', 'fi-FI', 'cs-CZ']);

export const SearchTypes = Object.freeze({
	ProjectName: 'projectName',
	OrderNumber: 'orderNumber',
	PurchaseOrderNumber: 'purchaseOrderNumber',
	PartNumber: 'partNumber',
	PartName: 'partName',
	QuoteNumber: 'quoteNumber',
	CadFilename: 'cadFilename',
	MoldId: 'esMoldId',
	Material: 'materialSeries',
	Color: 'color',
	UploadedBy: 'uploadedBy',
	OrderedBy: 'orderedBy',
	Part: 'part',
	None: 'none',
	ProductionPart: 'productionPart',
});

export const emptyCell: string = '--';

export const SharedWithYou = Object.freeze({
	projectId: 'SHAREDWITHYOU',
});

export const itarSecurityCode = 'UHIwdDAxdGFy';

export const confirmRenameOptions = Object.freeze({
	renamePart: 'renamePart',
	renameQuote: 'renameQuote',
});

export const duplicateProductionPartOptions = Object.freeze({
	changeConfiguration: 'changeConfiguration',
	keepConfiguration: 'keepConfiguration',
});

export const secopsCodes = Object.freeze({
	INSPECTDIR: 'INSPECTDIR',
});

export default {
	IMSample,
	CustomerSupplied,
	ItemStatus,
	LineItemCategory,
	StatusConfig,
	StatusChecklistItemCode,
	StatusChecklistItemState,
	PricingAdjustmentCodes,
	ProductionAgreementTypes,
	Regions,
	CurrencyCodes,
	ConfigurationClasses: ManufacturingServices,
	FieldLengths,
	ClaimTypes,
	MoreMenuOptions,
	ShippingTermsTypes,
	OboClaimTypes,
	OboEmployee,
	OrderFilterOptions,
	PartLifeStage,
	ManualReviewCodes,
	OrderProgressDefaults,
	SearchFilters,
	QuoteConcerns,
	UploadTypes,
	GroupedShippingConcerns,
	SessionKeys,
	EmptyGuid,
	ValidQuickLinkLanguages,
	QuoteNumberPlaceHolder,
	OrderOnHold,
	MaxHoldValues,
	SortOrderTypes,
	Carriers,
	LineItemOptions,
	ProductionAgreementsTransKeyMap,
	Experiments,
	MaterialEstimateResources,
	Suppliers,
	CarouselValues,
	MaxQuantities,
	AmPm,
	EmeaSourcedMaterials,
	ProtolabsAddresses,
	LockDownStatusTypes: LockDownStatusTypes,
	CapabilityConstants,
	FeatureFlags,
	HubsQuicklinkQuerystringVars,
	UnsupportedLanguages,
	SearchTypes,
	RouteNames,
	SharedWithYou,
	itarSecurityCode,
	confirmRenameOptions,
	secopsCodes,
};
